import { defineComponent as _defineComponent } from 'vue'
import { FifQuestion } from "./questions";
import FIFSurvey from "./FIFSurvey.vue";
import FIFOpenCalendar from "./Buttons/OpenCalendar.vue";
import FIFSetAnAppointment from "./Buttons/SetAnAppointment.vue";
import { FifScript } from "@/views/NewBusiness/FIF/FIFSurvey/FifScripts";
import { computed, onMounted, ref, watch } from "vue";

type ScriptConfig = {
	scriptType: FifScript;
	successMessage: string;
	failMessage: string;
	button?: "OPEN_CALENDAR" | "SET_AN_APPOINTMENT";
}


export default /*#__PURE__*/_defineComponent({
  __name: 'FIFSurveyForm',
  props: {
	questions: {
		type: Object as () => Record<FifScript, FifQuestion[]>,
		required: true,
	},
	scripts: {
		type: Object as () => Record<FifScript, boolean>,
		required: false,
	},
  	tryAgain: Boolean,
},
  emits: ["scriptFinished"],
  setup(__props, { expose, emit }: { emit: ({
	(e: "scriptFinished", value: FifScript): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props





const openPanel = ref<number | undefined>(undefined)
const currentScriptIndex = ref(0);
const surveyResult = ref<null | {
	message: string;
	button?: "OPEN_CALENDAR" | "SET_AN_APPOINTMENT";
	fifScript: FifScript;
}>(null);
const surveyResultMessages = computed(() => {
	return surveyResult.value?.message
		? surveyResult.value.message.split('\n').map((s) => s.trim()).filter(Boolean)
		: []
})

const reset = () => {
	currentScriptIndex.value = 0;
	surveyResult.value = null;
	openPanel.value = undefined
};

const resetAnswers = () => {
	surveyResult.value = null;
};

expose({ reset })

const scriptsOrdered = computed<FifScript[]>(() => {
	const result = [];
	if (!props.scripts || props.scripts[FifScript.DFL]) {
		result.push(FifScript.DFL);
	}
	if (!props.scripts || props.scripts[FifScript.QRS]) {
		result.push(FifScript.QRS);
	}
	if (!props.scripts || props.scripts[FifScript.IUL]) {
		result.push(FifScript.IUL);
	}
	if (!props.scripts || props.scripts[FifScript.IBC]) {
		result.push(FifScript.IBC);
	}
	if (!props.scripts || props.scripts[FifScript.Annuity]) {
		result.push(FifScript.Annuity);
	}
	return result;
});

const currentScript = computed(() => {
	return scriptsOrdered.value[currentScriptIndex.value];
});
const currentScriptQuestions = computed(() => {
	return props.questions[currentScript.value];
});
const cardTitle = computed(() => {
	return {
		[FifScript.DFL]: "Debt Free Life",
		[FifScript.QRS]: "Quility Retirement Services",
		[FifScript.IUL]: "Indexed Universal Life",
		[FifScript.IBC]: "Infinite Banking Concept",
		[FifScript.Annuity]: "Annuity Reset",
	}[currentScript.value];
});

const isLastScript = (script: FifScript): boolean => {
	return (
		scriptsOrdered.value.indexOf(script) === scriptsOrdered.value.length - 1
	);
};

const processScript = (
	answers: any[],
	{ scriptType, successMessage, failMessage, button }: ScriptConfig
): boolean => {
	if (currentScript.value !== scriptType) { return false }

	const isComplete = answers.some((a) => a.answer === true);
	if (isComplete) {
		surveyResult.value = {
			message: successMessage,
			button,
			fifScript: scriptType,
		};
		emit("scriptFinished", currentScript.value);
		return true;
	}

	if (!isLastScript(scriptType)) {
		currentScriptIndex.value += 1;
		return true;
	}

	surveyResult.value = {
		message: failMessage,
		fifScript: scriptType,
	};
	return true;
};

const FAIL_MESSAGE = `Thank you for your time. Please let me know if anything changes and we can review your position.`
const TAKE_A_LOOK_MESSAGE = `Let's take a look at their calendar and see when they are available...`

const scriptsConfig: Record<FifScript, ScriptConfig> = {
	[FifScript.DFL]: {
		scriptType: FifScript.DFL,
		successMessage: `
			We have an entire division dedicated to helping people understand how to eliminate debt using our Debt Free Life program. Our Subject Matter Experts can educate you on our debt elimination programs. You can do whatever you want with that information, but I KNOW you are going to be happy with the results as they have helped so many of my clients in the past.
			${TAKE_A_LOOK_MESSAGE}
		`,
		failMessage: FAIL_MESSAGE,
		button: "OPEN_CALENDAR",
	},
	[FifScript.QRS]: {
		scriptType: FifScript.QRS,
		successMessage: `
			We have an entire division solely based on education around retirement. Quility Financial Advisors are SUBJECT MATTER EXPERTS and they can educate you on your retirement accounts and help you fill in any gaps you might have. You can do whatever you want with that information, but I KNOW you are going to be happy with the information. They have helped out so many of my clients.
			${TAKE_A_LOOK_MESSAGE}
		`,
		failMessage: `At this time mortgage protection is the best solution for your client. We will review the file in a year for the PCS program`,
		button: "SET_AN_APPOINTMENT",
	},
	[FifScript.IUL]: {
		scriptType: FifScript.IUL,
		successMessage: `
			We have an entire division dedicated to helping people understand tax advantaged retirement. Our Subject Matter Experts can educate you on tax free, low risk retirement. You can do whatever you want with that information, but I KNOW you are going to be happy with the results as they have helped so many of my clients in the past.
			${TAKE_A_LOOK_MESSAGE}
		`,
		failMessage: FAIL_MESSAGE,
		button: "OPEN_CALENDAR",
	},
	[FifScript.Annuity]: {
		scriptType: FifScript.Annuity,
		successMessage: `
			We have an entire division dedicated to helping people understand protecting your retirement. Our Subject Matter Experts can educate you on protection from market loss. You can do whatever you want with that information, but I KNOW you are going to be happy with the results as they have helped so many of my clients in the past.
			${TAKE_A_LOOK_MESSAGE}
		`,
		failMessage: FAIL_MESSAGE,
		button: "OPEN_CALENDAR",
	},
	[FifScript.IBC]: {
		scriptType: FifScript.IBC,
		successMessage: `
			We have an entire division dedicated to helping people understand the benefits of becoming your own bank. Our Subject Matter Experts can educate you on infinite banking. You can do whatever you want with that information, but I KNOW you are going to be happy with the results as they have helped so many of my clients in the past.
			${TAKE_A_LOOK_MESSAGE}
		`,
		failMessage: ``,
		button: "OPEN_CALENDAR",
	},
}

const currentScriptConfig = computed(() => {
	return scriptsConfig[currentScript.value]
})

const onFinish = (answers: any[]): void => {
	// See: https://quility.atlassian.net/jira/software/projects/DEVST/boards/12?selectedIssue=DEVST-1695
	processScript(answers, currentScriptConfig.value)
};

const checkScriptHasQuestions = () => {
	if (!currentScriptQuestions.value?.length) {
		onFinish([{ answer: true }])
	}
}

onMounted(() => {
	checkScriptHasQuestions()
})

watch(() => props.scripts, () => {
	reset();
	checkScriptHasQuestions()
});

return { __sfc: true,props, emit, openPanel, currentScriptIndex, surveyResult, surveyResultMessages, reset, resetAnswers, scriptsOrdered, currentScript, currentScriptQuestions, cardTitle, isLastScript, processScript, FAIL_MESSAGE, TAKE_A_LOOK_MESSAGE, scriptsConfig, currentScriptConfig, onFinish, checkScriptHasQuestions, FIFSurvey, FIFOpenCalendar, FIFSetAnAppointment, FifScript }
}

})